/*
  Collection card mobile swipe gallery.

  Pipeline's <product-grid-item-variant> custom element bails out on mobile
  (`if (this.isMobile) return` in connectedCallback) — no slideshow at all.
  This script layers a CSS scroll-snap carousel on top, materializes any
  template-deferred images so all 5 are in the DOM, and renders dot indicators
  that update as the user swipes.

  Why a separate script (not a theme.js patch): theme.js is bundled. Adding a
  new file scoped to product cards keeps the diff tiny and lets us iterate
  without touching Pipeline's compiled output.
*/

(function () {
  "use strict";

  // Match Pipeline's mobile breakpoint (window.theme.sizes.medium = 768).
  // Using window check on every call so we react to orientation changes.
  function isMobile() {
    return window.innerWidth < 768;
  }

  /**
   * Materialize all deferred <template> images inside the card so they're real
   * <img> elements that the scroll-snap container can lay out side-by-side.
   * Pipeline wraps non-active images in <template> for desktop perf — on
   * mobile we want all of them rendered.
   */
  function materializeAllImages(imagesHolder) {
    const wrappers = imagesHolder.querySelectorAll("product-grid-item-image");
    wrappers.forEach((wrapper) => {
      const child = wrapper.firstElementChild;
      if (child && child.nodeName === "TEMPLATE") {
        // eagerLoad() is defined on the custom element class; it replaces
        // the <template> with its content and sets loading=eager. Calling
        // setAttribute triggers attributeChangedCallback → eagerLoad().
        wrapper.setAttribute("loading", "eager");
      }
    });
  }

  /**
   * Build the dot row and append it inside the card container.
   * Returns the array of dot elements for scroll-listener wiring.
   */
  function buildDots(card, count) {
    const existing = card.querySelector("[data-grid-mobile-dots]");
    if (existing) existing.remove();

    if (count < 2) return []; // nothing to indicate if there's only 1 image

    const dotsRow = document.createElement("div");
    dotsRow.className = "product-grid-item__mobile-dots";
    dotsRow.setAttribute("data-grid-mobile-dots", "");
    dotsRow.setAttribute("aria-hidden", "true");

    const dots = [];
    for (let i = 0; i < count; i++) {
      const dot = document.createElement("span");
      dot.className = "dot" + (i === 0 ? " is-active" : "");
      dotsRow.appendChild(dot);
      dots.push(dot);
    }

    // Append to .product-grid-item__container so the absolutely-positioned
    // dot row anchors to the card body and stays above the images.
    const container = card.querySelector(".product-grid-item__container") || card;
    container.appendChild(dotsRow);

    return dots;
  }

  /**
   * Wire scroll → active dot. Uses scrollLeft / clientWidth to compute the
   * current page; rAF-throttled to keep mobile scroll smooth.
   */
  function attachScrollListener(imagesHolder, dots) {
    if (!dots.length) return;
    let rafId = null;

    function update() {
      rafId = null;
      const pageWidth = imagesHolder.clientWidth;
      if (pageWidth <= 0) return;
      // Round to the nearest page (nearest snap target) instead of floor —
      // momentum scroll can land mid-snap before the snap completes.
      const idx = Math.round(imagesHolder.scrollLeft / pageWidth);
      const clamped = Math.max(0, Math.min(idx, dots.length - 1));
      for (let i = 0; i < dots.length; i++) {
        dots[i].classList.toggle("is-active", i === clamped);
      }
    }

    imagesHolder.addEventListener(
      "scroll",
      () => {
        if (rafId == null) rafId = requestAnimationFrame(update);
      },
      { passive: true },
    );
  }

  /**
   * Activate the mobile swipe carousel for one card.
   * Idempotent — safe to call multiple times.
   */
  function activateCard(card) {
    if (card.dataset.mobileSwipeActive === "1") return;
    const imagesHolder = card.querySelector("[data-grid-images]");
    if (!imagesHolder) return;

    materializeAllImages(imagesHolder);
    imagesHolder.setAttribute("data-mobile-swipe", "ready");

    // Count the rendered image wrappers AFTER materializing templates.
    const imageCount = imagesHolder.querySelectorAll("product-grid-item-image").length;
    if (imageCount < 2) return; // no swipe needed for 1 image

    const dots = buildDots(card, imageCount);
    attachScrollListener(imagesHolder, dots);

    card.dataset.mobileSwipeActive = "1";
  }

  /**
   * Activate every card on the page. Re-runnable when new cards appear
   * (e.g. infinite scroll, filter swaps).
   */
  function activateAll() {
    if (!isMobile()) return;
    const cards = document.querySelectorAll("[data-grid-item-variant]");
    cards.forEach(activateCard);
  }

  // Run once on DOM ready, again on full load (ensures custom elements have
  // upgraded), and once more after any "shopify:section:load" event in case
  // a section re-renders product cards (e.g. filter / sort).
  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(activateAll);
  window.addEventListener("load", activateAll);
  document.addEventListener("shopify:section:load", activateAll);

  // Re-evaluate on viewport changes (rotate phone / desktop dev tools resize)
  let lastIsMobile = isMobile();
  window.addEventListener("resize", () => {
    const nowMobile = isMobile();
    if (nowMobile && !lastIsMobile) activateAll();
    lastIsMobile = nowMobile;
  });
})();
